import { useId, useState, type ReactNode } from 'react';
import { shownColumns, type ColumnSort } from '@/domain';
import { strings } from '@/shared/strings';
import { useListMotion } from '@/ui/motion/useListMotion';
import { EmptyState } from '@/ui/feedback/EmptyState';
import { OrdersIcon } from '@/ui/icons';
import { CustomizeColumns, type TableCustomize } from './CustomizeColumns';
import styles from './ResponsiveTable.module.css';

export interface Column<Row> {
  key: string;
  label: string;
  /** Identifiers, times and money. Section 12. */
  mono?: boolean;
  /**
   * For single tokens that read as one thing. A date breaks after its hyphen
   * otherwise, and "2026-08-" over "05" is not a date anyone can scan.
   */
  nowrap?: boolean;
  align?: 'left' | 'right';
  sortable?: boolean;
  render: (row: Row) => ReactNode;
  /**
   * The value to sort by, when the rendered cell is not a scalar. A column that
   * renders a pipeline or a status dot still has something underneath it.
   */
  sortValue?: (row: Row) => string | number;
  /**
   * Something the rows already carry that the table does not show as it comes:
   * offered in Customize columns, and drawn once the reader ticks it. The
   * request of 8 October 2026 (second). It needs `customize`; a table nobody
   * can lay out never shows it.
   */
  optional?: boolean;
}

export interface ResponsiveTableProps<Row> {
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  caption: string;
  emptyHeading?: string;
  emptyDetail?: string;
  /** Header sorting is unreachable on a phone, so a select appears instead. */
  sortSelectOnMobile?: boolean;
  /**
   * The column the table is sorted by, when the screen holds that state.
   *
   * A case table's order is one piece of state shared with the preset select in
   * the filter bar, section 3 of the request of 13 September 2026, so the table
   * stops keeping its own copy as soon as a screen offers to keep it: two
   * pieces of state that both order the same rows is a bug waiting for the
   * first person to use both. Passing `onSortChange` is what hands it over.
   */
  sort?: ColumnSort | null;
  onSortChange?: (next: ColumnSort | null) => void;
  /**
   * A box leading every row, and one in the header for the rows on this page,
   * for the actions on a selection. Section 4 of the request of 24 September
   * 2026. The screen holds which rows are ticked; a box acts on its own and
   * never opens its row.
   */
  selection?: TableSelection<Row>;
  /**
   * The reader's own layout of this table, and how to change it: which columns,
   * in which order, and the rows a page. The request of 7 October 2026,
   * section 3. Given, the table draws the columns the reader chose in the
   * order they chose, on a phone's cards as on a desk's rows, and offers
   * Customize columns over itself. The first column is essential and is always
   * shown; `essential` names any other that is.
   */
  customize?: TableCustomize & { essential?: readonly string[] };
}

/** Which rows are ticked, held by the screen, which also knows what can be done with them. */
export interface TableSelection<Row> {
  /** The keys of the ticked rows, `rowKey`'s, which may include rows on other pages. */
  selected: ReadonlySet<string>;
  onToggle: (row: Row, selected: boolean) => void;
  onTogglePage: (rows: Row[], selected: boolean) => void;
  /** What a screen reader calls one row's box, which names the case: "Select JO-2041". */
  label: (row: Row) => string;
}

/** The column the boxes sit in. Not a column of the screen's, so it has no key it could clash with. */
const SELECT_KEY = '__select';

/**
 * The header's box: ticked when every row on the page is, half ticked when some
 * are, and a tick or an untick for all of them either way.
 */
function PageBox({ checked, partly, onChange }: { checked: boolean; partly: boolean; onChange: (next: boolean) => void }) {
  return (
    <input
      type="checkbox"
      className={styles.box}
      checked={checked}
      ref={(element) => {
        if (element !== null) element.indeterminate = partly;
      }}
      aria-label={strings.bulk.selectPage}
      onChange={(event) => onChange(event.target.checked)}
    />
  );
}

/**
 * The one table, section 11.4. Used by Orders now; Archive, Stage detail, Staff
 * and Users take the same component rather than growing their own.
 *
 * Below 760px the header hides and each row becomes a bordered card, with the
 * column label on the left of each value. Ninety percent of use is on phones
 * and a table that scrolls sideways on one is a defect.
 */
/** Whether an event started on something the reader was pressing in its own right. */
function insideAControl(target: EventTarget | null): boolean {
  return (
    target instanceof Element
    && target.closest('button, a, input, select, textarea, label') !== null
  );
}

export function ResponsiveTable<Row>({
  columns,
  rows,
  rowKey,
  onRowClick,
  caption,
  emptyHeading,
  emptyDetail,
  sortSelectOnMobile = false,
  sort: controlledSort,
  onSortChange,
  selection,
  customize,
}: ResponsiveTableProps<Row>) {
  const sortId = useId();
  const [ownSort, setOwnSort] = useState<ColumnSort | null>(null);

  const controlled = onSortChange !== undefined;
  const sort = controlled ? (controlledSort ?? null) : ownSort;
  const setSort = controlled ? onSortChange : setOwnSort;

  /*
   * The columns as the reader laid them out. Every column is still there to be
   * sorted by, a preset or a remembered order may name one that is hidden, so
   * the order below reads `columns` and only what is drawn reads `laidOut`.
   */
  const essential = new Set([columns[0]?.key, ...(customize?.essential ?? [])]);
  const listed = columns.map((column) => ({
    key: column.key,
    label: column.label,
    locked: essential.has(column.key),
    optional: column.optional === true,
  }));
  const laidOut =
    customize === undefined
      ? columns.filter((column) => column.optional !== true)
      : shownColumns(listed, customize.layout).map(
          (key) => columns.find((column) => column.key === key) as Column<Row>,
        );

  const sortable = laidOut.filter((column) => column.sortable === true);

  function valueFor(column: Column<Row>, row: Row): string | number {
    if (column.sortValue !== undefined) return column.sortValue(row);
    const rendered = column.render(row);
    return typeof rendered === 'string' || typeof rendered === 'number' ? rendered : '';
  }

  const ordered = (() => {
    if (sort === null) return rows;
    const column = columns.find((candidate) => candidate.key === sort.key);
    if (column === undefined) return rows;

    return [...rows].sort((a, b) => {
      const left = valueFor(column, a);
      const right = valueFor(column, b);
      const result =
        typeof left === 'number' && typeof right === 'number'
          ? left - right
          : String(left).localeCompare(String(right));
      return sort.direction === 'asc' ? result : -result;
    });
  })();

  // First load rises in, a new row arrives highlighted, a re-sorted row slides. Section 6, 5 October 2026.
  const body = useListMotion<HTMLTableSectionElement>(ordered.map(rowKey));

  function toggle(key: string) {
    setSort(
      sort !== null && sort.key === key
        ? { key, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<OrdersIcon />}
        heading={emptyHeading}
        detail={emptyDetail ?? strings.table.empty}
      />
    );
  }

  const ticked = selection === undefined ? 0 : rows.filter((row) => selection.selected.has(rowKey(row))).length;

  /*
   * The boxes lead every row, before the job order number, where the eye
   * starts. Labelled Select on a phone's card, where every cell says what it is.
   */
  const shown: Column<Row>[] =
    selection === undefined
      ? laidOut
      : [
          {
            key: SELECT_KEY,
            label: strings.bulk.select,
            render: (row) => (
              <input
                type="checkbox"
                className={styles.box}
                checked={selection.selected.has(rowKey(row))}
                aria-label={selection.label(row)}
                onChange={(event) => selection.onToggle(row, event.target.checked)}
              />
            ),
          },
          ...laidOut,
        ];

  return (
    <>
      {customize !== undefined && customize.quiet !== true && (
        <div className={styles.tools}>
          <CustomizeColumns columns={listed} customize={customize} />
        </div>
      )}
      {sortSelectOnMobile && sortable.length > 0 && (
        <div className={styles.sortBar}>
          <label htmlFor={sortId}>{strings.table.sortBy}</label>
          <select
            id={sortId}
            value={sort === null ? '' : `${sort.key}:${sort.direction}`}
            onChange={(event) => {
              const [key, direction] = event.target.value.split(':');
              if (key === undefined || key === '') setSort(null);
              else setSort({ key, direction: direction === 'desc' ? 'desc' : 'asc' });
            }}
          >
            <option value="">{strings.table.sortDefault}</option>
            {sortable.map((column) => (
              <optgroup key={column.key} label={column.label}>
                <option value={`${column.key}:asc`}>
                  {column.label} {strings.table.ascending}
                </option>
                <option value={`${column.key}:desc`}>
                  {column.label} {strings.table.descending}
                </option>
              </optgroup>
            ))}
          </select>
        </div>
      )}

      <div className={styles.wrap}>
        <table
          className={`${styles.table} ${styles.cards} ${onRowClick ? styles.clickable : ''}`}
        >
          <caption className="visually-hidden">{caption}</caption>
          <thead>
            <tr>
              {shown.map((column) => (
                <th
                  key={column.key}
                  className={
                    column.key === SELECT_KEY
                      ? styles.boxCell
                      : column.align === 'right'
                        ? styles.right
                        : undefined
                  }
                  aria-sort={
                    sort?.key === column.key
                      ? sort.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  {column.key === SELECT_KEY && selection !== undefined ? (
                    <PageBox
                      checked={ticked === rows.length}
                      partly={ticked > 0 && ticked < rows.length}
                      onChange={(next) => selection.onTogglePage(rows, next)}
                    />
                  ) : column.sortable === true ? (
                    <button className={styles.sortable} onClick={() => toggle(column.key)}>
                      {column.label}
                      {sort?.key === column.key && (
                        <span className={styles.direction}>
                          {sort.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody ref={body}>
            {ordered.map((row) => (
              <tr
                key={rowKey(row)}
                data-motion-key={rowKey(row)}
                className={selection?.selected.has(rowKey(row)) === true ? styles.ticked : undefined}
                /*
                 * A control inside the row acts on its own. Without this a row
                 * that carries buttons opens the case as well as pressing the
                 * button, which on Payments meant the Hold dialog opened on a
                 * screen that was already navigating away from it.
                 */
                onClick={
                  onRowClick === undefined
                    ? undefined
                    : (event) => {
                        if (insideAControl(event.target)) return;
                        onRowClick(row);
                      }
                }
                tabIndex={onRowClick === undefined ? undefined : 0}
                onKeyDown={
                  onRowClick === undefined
                    ? undefined
                    : (event) => {
                        if (insideAControl(event.target)) return;
                        if (event.key === 'Enter') onRowClick(row);
                      }
                }
              >
                {shown.map((column) => (
                  <td
                    key={column.key}
                    data-label={column.label}
                    className={[
                      column.key === SELECT_KEY ? styles.boxCell : '',
                      column.align === 'right' ? styles.right : '',
                      column.mono === true ? styles.mono : '',
                      column.nowrap === true ? styles.nowrap : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
